import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Send,
  Grid3X3,
  Check,
} from 'lucide-react';
import { careTokenHeaders } from '@/lib/care-token';
import TargetedCareMessengerPanel from '@/components/care-recipient/targeted-care-messenger-panel';
import QuickResponses from '@/components/messenger/quick-responses';

interface CareRecipientMessengerProps {
  hubId: string;
}

type CareJsonFetcher = <T = any>(url: string, init?: RequestInit) => Promise<T>;

export default function CareRecipientMessenger({ hubId }: CareRecipientMessengerProps) {
  const [selectedTargetedConversationId, setSelectedTargetedConversationId] = useState<string | null>(null);
  const [showChoiceBoards, setShowChoiceBoards] = useState(false);
  const queryClient = useQueryClient();

  async function fetchCareJson<T = any>(
    url: string,
    init: RequestInit = {},
  ): Promise<T> {
    const headers = new Headers(init.headers);
    for (const [name, value] of Object.entries(careTokenHeaders(hubId))) {
      headers.set(name, value);
    }
    headers.set('x-care-view-request', 'true');
    const response = await fetch(url, {
      ...init,
      headers,
      credentials: 'include',
    });
    if (!response.ok) {
      let message = response.statusText;
      try {
        const body = await response.json();
        message = body.message || message;
      } catch {
        // Keep the HTTP status text when the response is not JSON.
      }
      throw new Error(`${response.status}: ${message}`);
    }
    if (response.status === 204) return undefined as T;
    return await response.json() as T;
  }

  // Care View bootstrap data must use the Hub-scoped grant endpoint. This page
  // cannot rely on the caregiver's normal authenticated Hub endpoint.
  const { data: profileData = {} } = useQuery<any>({
    queryKey: ['/api/care/hub-profile', hubId],
    queryFn: () => fetchCareJson(`/api/care/hub-profile?hubId=${encodeURIComponent(hubId)}`),
    enabled: Boolean(hubId),
    staleTime: 300000,
  });

  const sendTargetedQuickResponse = useMutation({
    mutationFn: (content: string) => fetchCareJson(
      `/api/messenger/${hubId}/targeted-conversations/${selectedTargetedConversationId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      },
    ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [`/api/messenger/${hubId}/targeted-conversations/${selectedTargetedConversationId}/messages`],
        }),
        queryClient.invalidateQueries({
          queryKey: [`/api/messenger/${hubId}/targeted-conversations`],
        }),
      ]);
    },
  });

  return (
    <div className="space-y-3">
      <header className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <h2 className="font-semibold text-gray-900">
          {profileData?.careRecipient?.displayName || 'Care Recipient'} — Member Messages
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Choose the Hub Owner or an eligible Hub member to open a private conversation.
        </p>
      </header>

      <TargetedCareMessengerPanel
        hubId={hubId}
        onSelectedConversationChange={setSelectedTargetedConversationId}
      />

      <div className="flex flex-wrap gap-2">
        <QuickResponses
          disabled={!selectedTargetedConversationId || sendTargetedQuickResponse.isPending}
          onSelect={(content) => sendTargetedQuickResponse.mutate(content)}
        />
        <Button
          type="button"
          variant={showChoiceBoards ? 'default' : 'outline'}
          onClick={() => setShowChoiceBoards((visible) => !visible)}
        >
          Choice Boards
        </Button>
      </div>

      {!selectedTargetedConversationId && (
        <p className="text-sm text-gray-500">
          Select a person above before sending a quick response.
        </p>
      )}

      {showChoiceBoards && (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <ChoiceBoardsSection
            profileId={hubId}
            fetchCareJson={fetchCareJson}
          />
        </div>
      )}
    </div>
  );
}

function ChoiceBoardsSection({
  profileId,
  fetchCareJson,
}: {
  profileId: string;
  fetchCareJson: CareJsonFetcher;
}) {
  const [selectedChoiceId, setSelectedChoiceId] = useState<number | null>(null);
  const [selectedChoices, setSelectedChoices] = useState<{[boardId: string]: any[]}>({});
  const [boardChoices, setBoardChoices] = useState<{[boardId: string]: any[]}>({});

  useEffect(() => {
    console.log('[Messenger Choice Boards] Mounted with hubId:', profileId);
  }, [profileId]);

  const { data: choiceBoards = [], isLoading: choiceBoardsLoading, isError: choiceBoardsError } = useQuery({
    queryKey: [`/api/care/messenger/choice-boards`, profileId],
    queryFn: async () => {
      console.log(`[Messenger Choice Boards] Fetching boards for hubId: ${profileId}`);
      const res = await fetch(`/api/care/messenger/choice-boards?hubId=${profileId}`, { headers: careTokenHeaders(profileId) });
      if (!res.ok) {
        console.warn(`[Messenger Choice Boards] Fetch failed: HTTP ${res.status} for hubId ${profileId}`);
        throw new Error(`Choice boards request failed with HTTP ${res.status}`);
      }

      const data = await res.json();
      // The Care View endpoint returns an envelope, not a bare array:
      // { boards, timezone_used, timezone_source, auto_timezone }.
      // This is the same source and server-side availability filtering used
      // by the main Care View → Choice Boards section.
      const boards = (Array.isArray(data) ? data : (Array.isArray(data?.boards) ? data.boards : []))
        .map((board: any) => ({
          ...board,
          allowMultipleSelections: Number(board.maxChoicesAllowed ?? 1) > 1,
        }));
      console.log('[Messenger Choice Boards] Boards fetched:', {
        hubId: profileId,
        rawResponseIsArray: Array.isArray(data),
        rawBoardCount: Array.isArray(data) ? data.length : (Array.isArray(data?.boards) ? data.boards.length : 0),
        availableBoardCount: boards.length,
        boardIds: boards.map((board: any) => board.id),
        timezoneUsed: data?.timezone_used ?? null,
        timezoneSource: data?.timezone_source ?? null,
      });
      return boards;
    },
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (choiceBoardsLoading) return;
    if (choiceBoardsError) {
      console.warn(`[Messenger Choice Boards] Showing empty state because the request failed for hubId: ${profileId}`);
    } else if (choiceBoards.length === 0) {
      console.log(`[Messenger Choice Boards] Currently available: 0`);
      console.log(`[Messenger Choice Boards] Showing empty state because no boards passed the Care View availability filter for hubId: ${profileId}`);
    } else {
      console.log(`[Messenger Choice Boards] Currently available: ${choiceBoards.length}`, choiceBoards.map((board: any) => ({
        id: board.id,
        title: board.title,
      })));
    }
  }, [choiceBoards, choiceBoardsError, choiceBoardsLoading, profileId]);

  useEffect(() => {
    choiceBoards.forEach(async (board: any) => {
      try {
        const res = await fetch(`/api/care/choice-items?hubId=${profileId}&choiceBoardId=${board.id}&viaMessenger=true`, { headers: careTokenHeaders(profileId) });
        if (res.ok) {
          const items = await res.json();
          const mapped = items.map((item: any) => ({
            id: item.id,
            text: item.title || item.label || '',
            image: item.imageUrl || item.image || null,
            audioUrl: item.audioUrl || null,
          }));
          setBoardChoices(prev => ({ ...prev, [board.id]: mapped }));
        }
      } catch (err) {
        console.error(`Error fetching choices for board ${board.id}:`, err);
      }
    });
  }, [choiceBoards.map(b => b.id).join(',')]);

  const choiceSelectionMutation = useMutation({
    mutationFn: async (data: {
      choiceBoardId: string;
      choiceText: string;
      choiceImage?: string;
      choiceImages?: string[];
      boardTitle: string;
      choices?: string[];
    }) => {
      return await fetchCareJson(`/api/messenger/${profileId}/choice-selection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, selectionSource: 'messenger' }),
      });
    },
    onSuccess: (data, variables) => {
      setSelectedChoiceId(null);
      // Clear selected choices for the specific board
      if (variables.choices) {
        setSelectedChoices(prev => ({
          ...prev,
          [variables.boardTitle]: []
        }));
      }
    },
  });

  const handleSingleChoiceSelection = (choice: any, board: any) => {
    setSelectedChoiceId(choice.id);
    
    choiceSelectionMutation.mutate({
      choiceBoardId: board.id,
      choiceText: choice.text || 'Visual choice',
      choiceImage: choice.image,
      boardTitle: board.title,
    });
  };

  const handleMultipleChoiceToggle = (choice: any, board: any) => {
    const boardId = board.id.toString();
    const currentSelections = selectedChoices[boardId] || [];
    const isSelected = currentSelections.some(c => c.id === choice.id);

    if (isSelected) {
      // Remove choice
      setSelectedChoices(prev => ({
        ...prev,
        [boardId]: currentSelections.filter(c => c.id !== choice.id)
      }));
    } else {
      // Add choice
      setSelectedChoices(prev => ({
        ...prev,
        [boardId]: [...currentSelections, choice]
      }));
    }
  };

  const handleMultipleChoicesSubmit = (board: any) => {
    const boardId = board.id.toString();
    const selections = selectedChoices[boardId] || [];
    
    if (selections.length === 0) return;

    const choiceTexts = selections.map(choice => choice.text || 'Visual choice');
    const choiceImages = selections.filter(choice => choice.image).map(choice => choice.image);

    choiceSelectionMutation.mutate({
      choiceBoardId: board.id,
      choiceText: choiceTexts.join(', '),
      choiceImage: choiceImages[0], // Send first image for backward compatibility
      choiceImages: choiceImages, // Send all images for multi-select display
      boardTitle: board.title,
      choices: choiceTexts,
    });
  };

  const careViewChoiceBoards = choiceBoards;

  if (careViewChoiceBoards.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <Grid3X3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No choice boards available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {careViewChoiceBoards.map((board: any) => (
        <Card key={board.id} className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              {(board.coverImageUrl || (boardChoices[board.id] || [])[0]?.image) && (
                <img 
                  src={board.coverImageUrl || (boardChoices[board.id] || [])[0]?.image} 
                  alt={board.title}
                  className="w-12 h-12 object-contain rounded border bg-gray-50"
                />
              )}
              <div className="flex-1">
                <h5 className="font-medium text-sm">{board.title}</h5>
                <div className="flex items-center text-xs text-gray-500 mt-1">
                  <Grid3X3 className="w-3 h-3 mr-1" />
                  {(boardChoices[board.id] || []).length} choices
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {(boardChoices[board.id] || []).map((choice: any) => {
                const boardId = board.id.toString();
                const isSelectedInMultiSelect = board.allowMultipleSelections && 
                  (selectedChoices[boardId] || []).some(c => c.id === choice.id);
                const isSingleSelected = !board.allowMultipleSelections && selectedChoiceId === choice.id;
                
                return (
                  <button
                    key={choice.id}
                    type="button"
                    onClick={() => board.allowMultipleSelections 
                      ? handleMultipleChoiceToggle(choice, board)
                      : handleSingleChoiceSelection(choice, board)
                    }
                    disabled={choiceSelectionMutation.isPending}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      isSelectedInMultiSelect || isSingleSelected
                        ? 'border-green-500 bg-green-100 shadow-lg transform scale-105'
                        : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                    } ${choiceSelectionMutation.isPending ? 'opacity-50' : ''}`}
                  >
                    {choice.image && (
                      <img 
                        src={choice.image} 
                        alt={choice.text || 'Choice option'}
                        className="w-full h-16 object-contain rounded mb-2 bg-gray-50"
                      />
                    )}
                    {choice.text && (
                      <p className="text-xs text-center font-medium text-gray-700">
                        {choice.text}
                      </p>
                    )}
                    {(isSelectedInMultiSelect || isSingleSelected) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 rounded-lg">
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Submit Button for Multi-Select Boards */}
            {board.allowMultipleSelections && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {(selectedChoices[board.id.toString()] || []).length} selected
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleMultipleChoicesSubmit(board)}
                    disabled={choiceSelectionMutation.isPending || (selectedChoices[board.id.toString()] || []).length === 0}
                    className="text-xs px-3 py-1 h-8"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Send Choices
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}